import { formatDollars } from '@fruitful/fruitils';
import React, { useEffect, useState } from 'react';
import {
    BodyLargeEmphasized,
    BodyMediumEmphasized,
    BodySmallRegular,
    CheckIcon,
    DollarInput,
    IconButton,
    Sheet,
    SquarePenIcon,
    View,
    XIcon,
    XStack,
    YStack,
} from 'smoothie';

import { Button } from '../../common/components/GroveButtons';

export interface MoneyMapperSectionOverrideFooterProps {
    accentColor: string;
    title: string;
    sheetTitle: string;
    /** Headline amount shown when not editing (cents). */
    effectiveCents: number;
    /** Total from underlying line items (cents), used for the optional subline. */
    sourcesTotalCents: number;
    manualOverrideActive: boolean;
    showSourcesSubline: boolean;
    sourcesSublinePrefix: string;
    isSaving: boolean;
    isSavingEdit: boolean;
    isEditing: boolean;
    draftStr: string;
    setDraftStr: (v: string) => void;
    commitEdit: () => Promise<void>;
    removeManualOverride: () => Promise<void>;
    beginEdit: () => void;
    cancelEdit: () => void;
    editAmountButtonText?: string;
    removeManualButtonText?: string;
    /** When true, the edit affordance is disabled (e.g. missing underlying Money Map rule). */
    editDisabled?: boolean;
}

export const MoneyMapperSectionOverrideFooter = ({
    accentColor,
    title,
    sheetTitle,
    effectiveCents,
    sourcesTotalCents,
    manualOverrideActive,
    showSourcesSubline,
    sourcesSublinePrefix,
    isSaving,
    isSavingEdit,
    isEditing,
    draftStr,
    setDraftStr,
    commitEdit,
    removeManualOverride,
    beginEdit,
    cancelEdit,
    editAmountButtonText = 'Edit amount',
    removeManualButtonText = 'Remove manual',
    editDisabled = false,
}: MoneyMapperSectionOverrideFooterProps) => {
    const [menuOpen, setMenuOpen] = useState(false);

    const triggerDisabled =
        isSaving || isSavingEdit || isEditing || editDisabled;

    useEffect(() => {
        if (isEditing) {
            setMenuOpen(false);
        }
    }, [isEditing]);

    useEffect(() => {
        if (!manualOverrideActive) {
            setMenuOpen(false);
        }
    }, [manualOverrideActive]);

    return (
        <XStack
            width="100%"
            alignItems="center"
            justifyContent="space-between"
            gap={12}
            flexWrap="wrap"
        >
            <XStack alignItems="center" gap={8} flexShrink={0}>
                <View
                    width={40}
                    height={40}
                    backgroundColor={accentColor}
                    borderRadius={16}
                />
                <BodyMediumEmphasized color="$gray1200">
                    {title}
                </BodyMediumEmphasized>
            </XStack>

            {isEditing ? (
                <XStack
                    flex={1}
                    minWidth={0}
                    alignItems="center"
                    justifyContent="flex-end"
                    gap={10}
                    flexWrap="nowrap"
                >
                    <DollarInput
                        flexShrink={1}
                        flexGrow={0}
                        width={200}
                        maxWidth={240}
                        minWidth={120}
                        value={draftStr}
                        onChangeText={setDraftStr}
                        onSubmitEditing={() => {
                            void commitEdit();
                        }}
                        disabled={isSavingEdit || isSaving}
                        allowDecimals={false}
                    />
                    <XStack alignItems="center" gap={8} flexShrink={0}>
                        <IconButton
                            variant="primary"
                            icon={CheckIcon}
                            disabled={isSavingEdit || isSaving}
                            onPress={() => {
                                void commitEdit();
                            }}
                        />
                        <IconButton
                            variant="secondary"
                            icon={XIcon}
                            disabled={isSavingEdit || isSaving}
                            onPress={() => {
                                cancelEdit();
                            }}
                        />
                    </XStack>
                </XStack>
            ) : (
                <YStack
                    alignItems="flex-end"
                    gap={6}
                    flex={1}
                    minWidth={120}
                    flexShrink={1}
                >
                    <XStack alignItems="center" gap={10} flexShrink={0}>
                        <BodyLargeEmphasized color="$gray1200">
                            {formatDollars(effectiveCents / 100, true)}
                        </BodyLargeEmphasized>
                        <IconButton
                            variant="tertiary"
                            icon={SquarePenIcon}
                            disabled={triggerDisabled}
                            onPress={() => {
                                if (manualOverrideActive) {
                                    setMenuOpen(true);
                                } else {
                                    beginEdit();
                                }
                            }}
                        />
                    </XStack>
                    {showSourcesSubline ? (
                        <BodySmallRegular color="$gray1000">
                            {sourcesSublinePrefix}{' '}
                            {formatDollars(sourcesTotalCents / 100, true)}
                        </BodySmallRegular>
                    ) : null}
                </YStack>
            )}

            {manualOverrideActive ? (
                <Sheet
                    isOpen={menuOpen}
                    onClose={() => {
                        setMenuOpen(false);
                    }}
                    showCloseButton
                    title={sheetTitle}
                    disableDrag
                >
                    <YStack gap={8}>
                        <Button
                            variant="tertiary"
                            width="full"
                            onPress={() => {
                                setMenuOpen(false);
                                beginEdit();
                            }}
                        >
                            {editAmountButtonText}
                        </Button>
                        <Button
                            variant="tertiary"
                            width="full"
                            onPress={() => {
                                setMenuOpen(false);
                                void removeManualOverride();
                            }}
                        >
                            {removeManualButtonText}
                        </Button>
                    </YStack>
                </Sheet>
            ) : null}
        </XStack>
    );
};
